/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
  ConfigureListFilterResponse,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import deviceHubApi from '../../utils/api/device-hub';

/**
 * Fetch all brands from the API
 */
export const fetchAllBrands = createAsyncThunk(
  'brand/fetchAllBrands',
  async (query: any, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceHubApi.fetchBrands(removeEmptyKeys(query));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch brands',
          status: true,
        },
      });
      return rejectWithValue(error.response.data);
    }
  },
);

// Refresh brands without toggling loading state
export const refreshBrands = createAsyncThunk(
  'brand/refreshBrands',
  async (query: any, { dispatch, rejectWithValue }) => {
    try {
      const response = await deviceHubApi.fetchBrands(removeEmptyKeys(query));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to refresh brands',
          status: true,
        },
      });
      return rejectWithValue(error.response?.data);
    }
  },
);

/**
 * Edit an existing brand via PATCH
 */
export const editBrand = createAsyncThunk(
  'brand/editBrand',
  async (
    { id, data }: { id: string; data: Record<string, any> },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await deviceHubApi.updateBrand(id, data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to update brand',
          status: true,
        },
      });
      return rejectWithValue(error?.response?.data);
    }
  },
);

/**
 * Fetch brand filters
 */
export const fetchBrandFilters = createAsyncThunk<CommonFilterProperties[], any>(
  'brand/fetchBrandFilters',
  async (query, { dispatch }) => {
    try {
      const response = await deviceHubApi.brandFilter(query);
      console.log(response, 'response');
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response?.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

/**
 * Initial state for Brand slice
 */
const initialState = {
  brands: [] as any[],
  loading: false,
  totalResults: 0,
  totalPages: 0,
  page: 1,
  limit: 20,
  filters: availableFilters,
  isFilterUpdated: false,
  filterListLoading: false,
  sortBy: null,
  sortField: null,
};

const brandSlice = createSlice({
  name: 'brand',
  initialState,
  reducers: {
    /**
     * Set current page
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setBrandPage: (state, action) => {
      state.page = action.payload;
    },
    /**
     * Set limit
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setBrandLimit: (state, action) => {
      state.limit = action.payload;
    },
    /**
     * Update filters
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing filter updates.
     */
    updateBrandFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Disable filter applied flag
     * @param {any} state - The state
     */
    disableBrandFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Set sort by
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setBrandSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    /**
     * Set sort field
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setBrandSortField: (state, action) => {
      state.sortField = action.payload;
    },
  },
  /**
   * Handle asynchronous thunk actions
   * @param {any} builder - The builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllBrands.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllBrands.fulfilled, (state, action) => {
        state.loading = false;
        state.brands = action.payload.results || action.payload || [];
        state.totalResults =
          action.payload.totalResults ||
          (action.payload.results ? action.payload.results.length : 0);
        state.totalPages = action.payload.totalPages || 1;
        state.page = action.payload.page || state.page;
        state.limit = action.payload.limit || state.limit;
      })
      .addCase(fetchAllBrands.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchBrandFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchBrandFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          if (action.payload?.length > 0) {
            state.filters = [...(state.filters || []), ...action.payload];
          }
        },
      )
      .addCase(fetchBrandFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  setBrandPage,
  setBrandLimit,
  updateBrandFilters,
  disableBrandFilterApplied,
  setBrandSortBy,
  setBrandSortField,
} = brandSlice.actions;

export default brandSlice.reducer;
